import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useQuery, gql } from "@apollo/client";
import {
  Grid,
  Typography,
  TextField,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Paper,
  InputAdornment,
  Fade,
  Chip,
  Button,
  useTheme,
  alpha,
  useMediaQuery,
  styled,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import RecipeCard from "../Components/Cards/RecipeCard";
import { RecipeValidationProvider } from "../Context/RecipeValidationContext";
import Preloader from "../Components/Preloader/Preloader";
import { useNavigate } from "react-router-dom";
import ErrorPage from "./ErrorPage";
import BreadCrumb from "../Components/BreadCrumb";

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.8)
      : theme.palette.background.paper,
  backdropFilter: "blur(8px)",
  boxShadow: theme.shadows[2],
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const FilterButton = styled(ToggleButton)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  "&.Mui-selected": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.5, 1),
  },
}));

const ScrollContainer = styled(Box)({
  display: "flex",
  overflowX: "auto",
  scrollBehavior: "smooth",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  msOverflowStyle: "none",
  scrollbarWidth: "none",
});

export default function MyRecipes() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { loading, error, data, refetch } = useQuery(GET_MY_RECIPES);
  const [recipes, setRecipes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedType, setSelectedType] = useState("All");
  const [preloader, setPreloader] = useState(true);
  const navigate = useNavigate();
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const categoryRef = React.useRef(null);
  const typeRef = React.useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setPreloader(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (data?.allMyRecipes) {
      setRecipes(data.allMyRecipes);
    }
  }, [data]);

  // Memoized handlers
  const handleSearch = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleCategoryChange = useCallback((_, newCategory) => {
    setSelectedCategory(newCategory || "All");
  }, []);

  const handleTypeChange = useCallback((_, newType) => {
    setSelectedType(newType || "All");
  }, []);

  const scroll = useCallback((ref, direction) => {
    const scrollAmount = direction === "left" ? -200 : 200;
    ref.current?.scrollBy({ left: scrollAmount, behavior: "smooth" });
  }, []);

  const handleDeleteRecipe = useCallback(
    (deletedId) => {
      setRecipes((prev) => prev.filter((recipe) => recipe.id !== deletedId));
      setNotification({
        open: true,
        message: "Recipe has been permanently deleted",
        severity: "success",
      });
      refetch();
    },
    [refetch]
  );

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // Memoized filters and data
  const { filteredRecipes, uniqueCategories, uniqueTypes } = useMemo(() => {
    const filtered = recipes.filter((recipe) => {
      const categories = recipe.recipe_category
        .split(",")
        .map((cat) => cat.trim());
      const types = recipe.recipe_type.split(",").map((type) => type.trim());
      const matchesSearch = recipe.recipe_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === "All" || categories.includes(selectedCategory);
      const matchesType =
        selectedType === "All" || types.includes(selectedType);

      return matchesSearch && matchesCategory && matchesType;
    });

    const getUniqueValues = (items, key) => [
      "All",
      ...new Set(
        items.flatMap((item) => item[key].split(",").map((val) => val.trim()))
      ),
    ];

    return {
      filteredRecipes: filtered,
      uniqueCategories: getUniqueValues(recipes, "recipe_category"),
      uniqueTypes: getUniqueValues(recipes, "recipe_type"),
    };
  }, [recipes, searchTerm, selectedCategory, selectedType]);

  if (loading || preloader) return <Preloader />;
  if (error) return <ErrorPage message="Error loading recipes" />;

  return (
    <Fade in timeout={500}>
      <Box>
        <BreadCrumb
          links={[{ label: "Dashboard", href: "/dashboard" }]}
          currentPage="Manage Recipes"
        />

        <StyledPaper elevation={0}>
          {/* Header Section */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              My Recipes
              <Typography component="span" color="text.secondary" ml={1}>
                ({filteredRecipes.length})
              </Typography>
            </Typography>

            <Box display="flex" gap={2}>
              <TextField
                placeholder="Search recipes..."
                size="small"
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: "100%", sm: 200 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/add-recipe")}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: 2,
                }}
              >
                Add Recipe
              </Button>
            </Box>
          </Box>

          {/* Filters Section */}
          <Box mb={3}>
            <Typography variant="subtitle2" color="text.secondary" mb={1}>
              <FilterIcon sx={{ verticalAlign: "middle", mr: 0.5 }} />
              Filters
            </Typography>

            {/* Categories */}
            <Box display="flex" alignItems="center" mb={2}>
              <IconButton
                onClick={() => scroll(categoryRef, "left")}
                size="small"
                sx={{ display: { xs: "none", sm: "flex" } }}
              >
                <ChevronLeftIcon />
              </IconButton>

              <ScrollContainer ref={categoryRef}>
                <ToggleButtonGroup
                  value={selectedCategory}
                  exclusive
                  onChange={handleCategoryChange}
                  aria-label="Recipe Category"
                >
                  {uniqueCategories.map((category) => (
                    <FilterButton key={category} value={category}>
                      {category}
                    </FilterButton>
                  ))}
                </ToggleButtonGroup>
              </ScrollContainer>

              <IconButton
                onClick={() => scroll(categoryRef, "right")}
                size="small"
                sx={{ display: { xs: "none", sm: "flex" } }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {/* Types */}
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={() => scroll(typeRef, "left")}
                size="small"
                sx={{ display: { xs: "none", sm: "flex" } }}
              >
                <ChevronLeftIcon />
              </IconButton>

              <ScrollContainer ref={typeRef}>
                <ToggleButtonGroup
                  value={selectedType}
                  exclusive
                  onChange={handleTypeChange}
                  aria-label="Recipe Type"
                >
                  {uniqueTypes.map((type) => (
                    <FilterButton key={type} value={type}>
                      {type}
                    </FilterButton>
                  ))}
                </ToggleButtonGroup>
              </ScrollContainer>

              <IconButton
                onClick={() => scroll(typeRef, "right")}
                size="small"
                sx={{ display: { xs: "none", sm: "flex" } }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Recipe Grid */}
          <Grid container spacing={3}>
            {filteredRecipes.length > 0 ? (
              filteredRecipes.map((recipe) => (
                <Grid item xs={12} sm={6} md={4} key={recipe.id}>
                  <RecipeValidationProvider recipe_id={recipe.id}>
                    <RecipeCard
                      recipe={recipe}
                      onDelete={handleDeleteRecipe}
                      refetch={refetch}
                    />
                  </RecipeValidationProvider>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  py={8}
                >
                  <Typography variant="h6" color="text.secondary" mb={2}>
                    No recipes found
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => navigate("/add-recipe")}
                  >
                    Create your first recipe
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </StyledPaper>

        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            variant="filled"
            elevation={6}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
}

const GET_MY_RECIPES = gql`
  query GetRecipes {
    allMyRecipes {
      id
      recipe_name
      viewsCount
      sharedCount
      likedCount
      cookedCount
      downloadedCount
      created_by
      duration
      recipe_category
      recipe_type
      recipe_status
      cook_time
      prep_time
      recipe_visiblity
      recipe_image
      profile_pic
    }
  }
`;
